import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";

import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

import UiTable from "../../../components/table/Table.component";
import UiButton from "../../../components/button/Button";
import { EndpointsService } from "../../../services/endpoints/endpoints.service";
import { ColumnBodyOptions } from "primereact/column";
import {
	TableColumn,
	TableColumnTemplate,
	TemplateOptions,
} from "../../../models/components/table/table-column.model";
import { TableStyle } from "../../../models/components/table/table.model";

type ToggleExpand = (row: unknown, colBody: ColumnBodyOptions) => void;
import { TableMessages } from "../../../models/components/table/table-message.model";
import {
	FieldMessageSeverity,
	UiFieldMessageProps,
} from "../../../models/components/field-message.model";
import { TablePagination } from "../../../models/components/table/table-pagination.model";

import { PageProps } from "../../../models/routing.model";
import { MessageTemplate } from "../../../models/entities/message-template.model";
import {
	MessageTemplateRow,
	MessageTemplateScope,
	MessageTemplateVariantRow,
	getMessageTemplateScope,
} from "../../../models/pages/settings/message-templates-row.model";
import { useMessageTemplates } from "../../../hooks/useMessageTemplates";
import { useProvidersInfo } from "../../../hooks/useProvidersInfo";
import { useInstitutionsDetailed } from "../../../hooks/useInstitutionsDetailed";

const LIST_ROUTE = "/settings/message-templates";

const SettingsMessageTemplates = (props: PageProps) => {
	const { t } = useTranslation("common");
	const navigate = useNavigate();

	const templates = useMessageTemplates();
	const institutions = useInstitutionsDetailed();
	// Prefetch providers so the COACH column template's lookup hits the cache.
	useProvidersInfo();

	const toast = useRef<Toast>(null);

	const handleDelete = (id: number, name: string) =>
		confirmDialog({
			message: t("MESSAGE_TEMPLATES.CONFIRM.DELETE_MESSAGE", { name }),
			header: t("MESSAGE_TEMPLATES.CONFIRM.DELETE_HEADER"),
			icon: "pi pi-trash",
			acceptClassName: "p-button-danger",
			acceptLabel: t("MESSAGE_TEMPLATES.CONFIRM.DELETE_ACCEPT"),
			rejectLabel: t("MESSAGE_TEMPLATES.CONFIRM.CANCEL"),
			accept: async () => {
				try {
					await EndpointsService.dataRetriever.deleteMessageTemplate({ id });
					await templates.invalidate();
					toast.current?.show({
						severity: "success",
						summary: t("MESSAGE_TEMPLATES.TOAST.DELETED"),
					});
				} catch (error) {
					toast.current?.show({
						severity: "error",
						summary: t("MESSAGE_TEMPLATES.TOAST.DELETE_ERROR"),
						detail: (error as AxiosError)?.message,
					});
				}
			},
		});

	const institutionNameById = useMemo(() => {
		const map = new Map<number, string>();
		(institutions.data ?? []).forEach((institution) =>
			map.set(institution.institutionId, institution.institutionName)
		);
		return map;
	}, [institutions.data]);

	const [filterName, setFilterName] = useState("");
	const [filterLanguage, setFilterLanguage] = useState<string | null>(null);
	const [filterScope, setFilterScope] = useState<MessageTemplateScope | null>(null);

	const languageOptions = useMemo(() => {
		const fromTemplates = (templates.data ?? [])
			.map((template) => template.languageCode)
			.filter((code): code is string => !!code);
		const set = new Set<string>(["ENGLISH", "SPANISH", ...fromTemplates]);
		return Array.from(set).map((code) => ({ label: code, value: code }));
	}, [templates.data]);

	const scopeFilterOptions = useMemo(
		() =>
			(["GLOBAL", "COACH", "INSTITUTION", "COACH_INSTITUTION"] as MessageTemplateScope[]).map(
				(scope) => ({
					label: t(`MESSAGE_TEMPLATES.SCOPE_FULL.${scope}`),
					value: scope,
				})
			),
		[t]
	);

	const filteredTemplates = useMemo(() => {
		const nameTerm = filterName.trim().toLowerCase();
		return (templates.data ?? []).filter((template) => {
			if (nameTerm && !template.name.toLowerCase().includes(nameTerm)) return false;
			if (filterLanguage && template.languageCode !== filterLanguage) return false;
			if (filterScope && getMessageTemplateScope(template) !== filterScope) return false;
			return true;
		});
	}, [templates.data, filterName, filterLanguage, filterScope]);

	const goToEdit = (templateId: number) => navigate(`${LIST_ROUTE}/${templateId}`);
	const goToNew = () => navigate(`${LIST_ROUTE}/new`);

	// Group templates by name. Single-variant names render as a normal row;
	// multi-variant names render once as a group row with an expander that
	// reveals each variant.
	const rows = useMemo<MessageTemplateRow[]>(() => {
		const groups = new Map<string, MessageTemplate[]>();
		filteredTemplates.forEach((template) => {
			const list = groups.get(template.name) ?? [];
			list.push(template);
			groups.set(template.name, list);
		});

		return Array.from(groups.entries()).map(
			([name, variants]) =>
				new MessageTemplateRow({
					name,
					variants,
					institutionName: variants[0].institutionId
						? (institutionNameById.get(variants[0].institutionId) ?? "")
						: "",
					onEdit: goToEdit,
					onDelete: handleDelete,
				})
		);
	}, [filteredTemplates, institutionNameById]);

	const columns: TableColumn[] = [
		{
			field: "name",
			title: "MESSAGE_TEMPLATES.LIST.COLUMNS.NAME",
			sortable: true,
			style: { width: "50%" },
		},
		{
			field: "languageCode",
			title: "MESSAGE_TEMPLATES.LIST.COLUMNS.LANGUAGE",
			sortable: true,
			style: { width: "15%" },
		},
		{
			field: "coach",
			title: "MESSAGE_TEMPLATES.LIST.COLUMNS.COACH",
			template: TableColumnTemplate.COACH,
			style: { width: "15%" },
		},
		{
			field: "institutionName",
			title: "MESSAGE_TEMPLATES.LIST.COLUMNS.INSTITUTION",
			style: { width: "15%" },
		},
		{
			field: "scopeTag",
			title: "MESSAGE_TEMPLATES.LIST.COLUMNS.SCOPE",
			template: TableColumnTemplate.TAG,
			style: { width: "15%" },
		},
		{
			field: "expandCollapse",
			template: TableColumnTemplate.EXPANDER,
			alignment: "center",
			// Wide enough to keep the edit + delete buttons side by side instead
			// of wrapping the delete button below the pencil; nowrap guards it.
			style: { width: "10%", whiteSpace: "nowrap" },
			templateOptions: {
				// One actions column for both flows: the expander chevron for
				// multi-variant rows; the edit button for single-variant rows.
				// NOTE: the existing `expanderTemplate` type signature in
				// TemplateOptions says `(isExpanded, toggleExpand)` but the actual
				// implementation in TableCell.component passes the rowData as the
				// first arg — hence the cast.
				expanderTemplate: ((rowData: MessageTemplateRow, toggleExpand: ToggleExpand) =>
					rowData.expandCollapse ? (
						<UiButton
							type="button"
							className="p-button-text neutral"
							icon="pi pi-chevron-down"
							onClick={() => toggleExpand(rowData, {} as ColumnBodyOptions)}
						/>
					) : rowData.representative.id !== null ? (
						<>
							<UiButton
								type="button"
								className="p-button-text neutral"
								icon="pi pi-pencil"
								onClick={() => goToEdit(rowData.representative.id as number)}
							/>
							<UiButton
								type="button"
								className="p-button-text neutral"
								icon="pi pi-trash"
								onClick={() =>
									handleDelete(
										rowData.representative.id as number,
										rowData.representative.name
									)
								}
							/>
						</>
					) : null) as unknown as TemplateOptions["expanderTemplate"],
			},
		},
	];

	const message: UiFieldMessageProps | undefined = useMemo(() => {
		const messages = new TableMessages();
		if (templates.isLoading) {
			return { severity: FieldMessageSeverity.LOADING, label: messages.loading };
		}
		if (templates.isError) {
			return { severity: FieldMessageSeverity.DANGER, label: messages.error };
		}
		if (rows.length === 0) {
			return { severity: FieldMessageSeverity.INFO, label: messages.empty };
		}
		return undefined;
	}, [templates.isLoading, templates.isError, rows.length]);

	const [pagination, setPagination] = useState<TablePagination>({
		first: 0,
		rows: 10,
		page: 0,
		sortField: "name",
		sortOrder: 1,
		search: undefined,
		filters: undefined,
	});

	// Columns for the nested variants table — same shape and widths as the
	// parent so each variant cell aligns under its parent header. The first
	// column ("name") stays blank to take the space under the parent name
	// header. No expander column here (variants are leaves), and the actions
	// column hosts the per-variant Edit button via TableColumnTemplate.BUTTONS.
	const variantColumns: TableColumn[] = [
		{ field: "name", sortable: false, style: { width: "50%" } },
		{
			field: "languageCode",
			sortable: false,
			style: { width: "15%" },
		},
		{
			field: "coach",
			sortable: false,
			template: TableColumnTemplate.COACH,
			style: { width: "15%" },
		},
		{
			field: "institutionName",
			sortable: false,
			style: { width: "15%" },
		},
		{
			field: "scopeTag",
			sortable: false,
			template: TableColumnTemplate.TAG,
			style: { width: "15%" },
		},
		{
			field: "actionsTemplate",
			sortable: false,
			template: TableColumnTemplate.BUTTONS,
			alignment: "center",
			// Match the parent actions column width so variant cells stay aligned
			// under the parent headers.
			style: { width: "10%" },
		},
	];

	// Render the variants of a multi-variant group as a nested UiTable — same
	// pattern BiometricsList uses for its expansion content. The nested table's
	// columns reuse the parent column widths so cells line up under the parent
	// headers, and TableStyle.NESTED hides the duplicate header row.
	const expandedTemplate = (row: MessageTemplateRow) => {
		const variantRows = row.variants.map(
			(variant) =>
				new MessageTemplateVariantRow({
					template: variant,
					institutionName: variant.institutionId
						? (institutionNameById.get(variant.institutionId) ?? "")
						: "",
					onEdit: goToEdit,
					onDelete: handleDelete,
				})
		);

		return (
			<UiTable
				dataKey="id"
				type={TableStyle.NESTED}
				useAsCard={false}
				columns={variantColumns}
				value={variantRows}
				paginator={false}
				pagination={{
					first: 0,
					rows: variantRows.length,
					page: 0,
					sortField: undefined,
					sortOrder: 1,
					search: undefined,
					filters: undefined,
				}}
				style={{ padding: "0rem" }}
			/>
		);
	};

	return (
		<div
			className="mt-page"
			id={props.id}
		>
			<Toast ref={toast} />
			<ConfirmDialog />
			<UiTable
				useAsCard
				dataKey="id"
				title="MESSAGE_TEMPLATES.LIST.TITLE"
				customHeader={
					<UiButton
						type="button"
						className="p-button-rounded p-button-sm"
						icon="pi pi-plus"
						label="MESSAGE_TEMPLATES.LIST.NEW"
						onClick={goToNew}
					/>
				}
				secondaryHeader={
					<div className="mt-list-filters">
						<span className="p-input-icon-left mt-list-filter mt-list-filter--name">
							<i className="pi pi-search" />
							<InputText
								value={filterName}
								onChange={(event) => setFilterName(event.target.value)}
								placeholder={t("MESSAGE_TEMPLATES.LIST.FILTER_NAME")}
							/>
						</span>
						<Dropdown
							className="mt-list-filter"
							value={filterLanguage}
							options={languageOptions}
							onChange={(event) => setFilterLanguage(event.value ?? null)}
							placeholder={t("MESSAGE_TEMPLATES.LIST.FILTER_ALL_LANGUAGES")}
							showClear
						/>
						<Dropdown
							className="mt-list-filter"
							value={filterScope}
							options={scopeFilterOptions}
							onChange={(event) => setFilterScope(event.value ?? null)}
							placeholder={t("MESSAGE_TEMPLATES.LIST.FILTER_ALL_SCOPES")}
							showClear
						/>
					</div>
				}
				columns={columns}
				value={rows}
				message={message}
				// Only paginate once there's more than a page of rows. Otherwise
				// the paginator just hangs below a short/empty list with nothing
				// to page through.
				paginator={rows.length > pagination.rows}
				pagination={pagination}
				paginationFn={(event) => {
					setPagination({ ...pagination, ...event });
				}}
				expandedTemplate={expandedTemplate}
			/>
		</div>
	);
};

export default SettingsMessageTemplates;
